import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import clsx from 'clsx';

// X mark icon for close button
const XMarkIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const styles = {
  backdrop: 'fixed inset-0 bg-black/50 backdrop-blur-sm',
  container: 'fixed inset-0 z-50 overflow-y-auto',
  panel: [
    'relative w-full max-w-md p-6 mx-auto mt-8 mb-8 overflow-hidden rounded-xl bg-white shadow-lg',
    'dark:bg-zinc-900 dark:ring-1 dark:ring-white/10',
    'transform transition-all',
  ],
  closeButton: [
    'absolute top-3 right-3 flex h-8 w-8 items-center justify-center rounded-md text-gray-400 hover:text-gray-500',
    'dark:text-gray-500 dark:hover:text-gray-400',
  ],
  title: 'text-lg font-semibold leading-6 text-gray-900 dark:text-white',
  description: 'mt-2 text-sm text-gray-600 dark:text-gray-400',
  sizes: {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl',
    '5xl': 'max-w-5xl',
    full: 'max-w-full mx-4',
  },
};

export const Modal = ({
  isOpen = false,
  onClose,
  title,
  description,
  children,
  className = '',
  size = 'md',
  closeOnOverlayClick = true,
  closeButton = true,
  initialFocus,
  ...props
}) => {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog 
        as="div"
        className={styles.container}
        onClose={closeOnOverlayClick ? onClose : () => {}}
        initialFocus={initialFocus}
        {...props}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className={styles.backdrop} aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel 
                className={clsx(
                  styles.panel,
                  styles.sizes[size],
                  className
                )}
              >
                {closeButton && (
                  <button
                    type="button"
                    className={styles.closeButton}
                    onClick={onClose}
                    aria-label="Close"
                  >
                    <XMarkIcon />
                  </button>
                )}

                {title && (
                  <Dialog.Title className={styles.title}>
                    {title}
                  </Dialog.Title>
                )}

                {description && (
                  <Dialog.Description className={styles.description}>
                    {description}
                  </Dialog.Description>
                )}

                {title || description ? <div className="mt-4">{children}</div> : children}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

// Modal with predefined sizes
export const SmallModal = (props) => <Modal size="sm" {...props} />;
export const MediumModal = (props) => <Modal size="md" {...props} />;
export const LargeModal = (props) => <Modal size="lg" {...props} />;
export const ExtraLargeModal = (props) => <Modal size="xl" {...props} />;

// Modal subcomponents
export const ModalContent = ({ children, className = '', ...props }) => (
  <div className={clsx('mt-6', className)} {...props}>
    {children}
  </div>
);

export const ModalFooter = ({ children, className = '', ...props }) => (
  <div className={clsx('mt-6 flex justify-end gap-3', className)} {...props}>
    {children}
  </div>
);
